import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, Clock, MapPin, ShieldCheck, Sparkles, Sun, Building2, Lightbulb, Plus, Minus, Star } from 'lucide-react';
import { useCourt, calculateCourtPrice, useRentalItems, useCourtReviews, useCourtDaySlots, type CourtSlot } from '../../hooks/useAPI';
import FavoriteButton from './FavoriteButton';

// Photo shape drifted: admin writes plain URL strings, legacy rows are
// { url, storage_path, is_primary } objects. Accept both.
function photoUrl(p: unknown): string | null {
  if (!p) return null;
  if (typeof p === 'string') return p;
  if (typeof p === 'object' && 'url' in (p as any) && typeof (p as any).url === 'string') return (p as any).url;
  return null;
}

interface CourtDetailScreenProps {
  courtId: string;
  onNavigate: (screen: any, params?: any) => void;
  onBook: (payload: {
    courtId: string;
    startAt: string;
    endAt: string;
    clubId: string;
    rentals?: Array<{ rentalItemId: string; quantity: number }>;
  }) => void;
}

export default function CourtDetailScreen({ courtId, onNavigate, onBook }: CourtDetailScreenProps) {
  const { data: court } = useCourt(courtId);
  const c = court as any;
  const { data: rentals } = useRentalItems(c?.club_id);
  const rentalItems = (rentals as any[]) || [];
  const { data: reviews } = useCourtReviews(courtId);
  const reviewList = (reviews as any[]) || [];
  const avgRating = reviewList.length
    ? reviewList.reduce((s, r) => s + r.rating, 0) / reviewList.length
    : null;
  const [rentalQty, setRentalQty] = useState<Record<string, number>>({});

  // next 7 days of dates
  const dates = useMemo(
    () => Array.from({ length: 7 }, (_, i) => {
      const d = new Date(); d.setDate(d.getDate() + i);
      return d.toISOString().split('T')[0];
    }),
    [],
  );
  const [selectedDate, setSelectedDate] = useState(dates[0]);

  // Server-computed slot list. Each row carries availability + reason
  // (Too soon / Closed / Blocked / Booked) so the picker can render
  // unbookable slots crossed out instead of letting the user click
  // through to a 409 "Slot not available" at checkout.
  const slotMinutes = c?.slot_minutes ?? 60;
  const { data: slotData } = useCourtDaySlots(courtId, selectedDate);
  const slotRows = (slotData ?? []) as CourtSlot[];
  // Convert the RPC's UTC start_at into the local "HH:MM" string the
  // existing UI uses as the slot key.
  const slots = useMemo(
    () => slotRows.map((r) => {
      const local = new Date(r.start_at);
      const hh = String(local.getHours()).padStart(2, '0');
      const mm = String(local.getMinutes()).padStart(2, '0');
      return { time: `${hh}:${mm}`, available: r.available, reason: r.reason };
    }),
    [slotRows],
  );

  const [selectedSlot, setSelectedSlot] = useState<string | null>(null);
  const [durationSlots, setDurationSlots] = useState(1); // number of consecutive slots
  const [quote, setQuote] = useState<{ total: number; currency: string } | null>(null);
  const [quoting, setQuoting] = useState(false);

  const toISO = (dateStr: string, time: string) => new Date(`${dateStr}T${time}:00`).toISOString();

  const startAt = selectedSlot ? toISO(selectedDate, selectedSlot) : null;
  const endAt = useMemo(() => {
    if (!startAt) return null;
    const d = new Date(startAt);
    d.setMinutes(d.getMinutes() + slotMinutes * durationSlots);
    return d.toISOString();
  }, [startAt, slotMinutes, durationSlots]);

  const durationHours = (slotMinutes * durationSlots) / 60;
  const rentalTotal = rentalItems.reduce((sum, item) => {
    const qty = rentalQty[item.id] ?? 0;
    if (!qty) return sum;
    const mult = item.price_unit === 'per_hour' ? durationHours : 1;
    return sum + Number(item.unit_price) * qty * mult;
  }, 0);
  const rentalsArray = Object.entries(rentalQty)
    .filter(([, q]) => q > 0)
    .map(([rentalItemId, quantity]) => ({ rentalItemId, quantity }));

  useEffect(() => {
    setSelectedSlot(null);
    setQuote(null);
  }, [selectedDate]);

  useEffect(() => {
    if (!startAt || !endAt) { setQuote(null); return; }
    setQuoting(true);
    calculateCourtPrice({ courtId, startAt, endAt })
      .then((q) => setQuote({ total: q.total, currency: q.currency }))
      .catch(() => setQuote(null))
      .finally(() => setQuoting(false));
  }, [courtId, startAt, endAt]);

  if (!c) {
    return (
      <div className="w-full h-screen bg-background flex items-center justify-center text-sm text-muted-foreground">
        Loading court…
      </div>
    );
  }

  const club = c.clubs;

  return (
    <div className="w-full min-h-screen bg-background pb-32">
      <div className="relative aspect-[16/9] bg-secondary">
        {(() => {
          const src = photoUrl(c.photos?.[0]) ?? photoUrl(club?.photos?.[0]);
          return src ? <img src={src} alt="" className="w-full h-full object-cover" /> : null;
        })()}
        <button
          onClick={() => onNavigate('courts')}
          className="absolute top-4 left-4 w-10 h-10 rounded-full bg-white/90 flex items-center justify-center"
        >
          <ChevronLeft className="w-5 h-5" />
        </button>
        <div className="absolute top-4 right-4">
          <FavoriteButton target={{ kind: 'court', id: c.id }} variant="floating" />
        </div>
      </div>

      <div className="px-6 pt-5 pb-4">
        <h1 className="text-2xl">{club?.name}</h1>
        <div className="text-sm text-muted-foreground mt-0.5">{c.name}</div>
        {club?.address && (
          <div className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            {club.address}
          </div>
        )}
        <div className="flex gap-2 flex-wrap mt-3">
          <Spec icon={c.is_indoor ? Building2 : Sun} label={c.is_indoor ? 'Indoor' : 'Outdoor'} />
          <Spec icon={Sparkles} label={c.surface} />
          {c.has_lights && <Spec icon={Lightbulb} label="Lights" />}
        </div>
      </div>

      <div className="px-6 space-y-4">
        <div>
          <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#1A6B4F]" />
            Pick a date
          </h3>
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {dates.map((d) => {
              const date = new Date(d);
              const sel = d === selectedDate;
              return (
                <button
                  key={d}
                  onClick={() => setSelectedDate(d)}
                  className={`flex-shrink-0 w-16 h-20 rounded-2xl flex flex-col items-center justify-center ${
                    sel ? 'bg-[#1A6B4F] text-white' : 'bg-card border border-border'
                  }`}
                >
                  <div className={`text-[11px] uppercase ${sel ? 'text-white/80' : 'text-muted-foreground'}`}>
                    {date.toLocaleDateString('en-US', { weekday: 'short' })}
                  </div>
                  <div className="text-xl font-medium leading-none mt-0.5">{date.getDate()}</div>
                  <div className={`text-[11px] mt-1 ${sel ? 'text-white/80' : 'text-muted-foreground'}`}>
                    {date.toLocaleDateString('en-US', { month: 'short' })}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <h3 className="text-sm font-medium mb-2">Start time</h3>
          {slots.length === 0 ? (
            <div className="text-xs text-muted-foreground bg-secondary/40 rounded-xl px-3 py-3">
              No bookable slots on this date — the club is closed all day.
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2">
              {slots.map((s) => {
                const sel = selectedSlot === s.time;
                const disabled = !s.available;
                return (
                  <button
                    key={s.time}
                    type="button"
                    onClick={() => { if (!disabled) setSelectedSlot(s.time); }}
                    disabled={disabled}
                    title={disabled ? (s.reason ?? 'Unavailable') : undefined}
                    aria-disabled={disabled}
                    className={`h-11 rounded-xl text-sm transition-all relative ${
                      sel
                        ? 'bg-[#1A6B4F] text-white shadow-sm'
                        : disabled
                          ? 'bg-secondary/40 text-muted-foreground/60 line-through cursor-not-allowed'
                          : 'bg-card border border-border hover:border-[#1A6B4F]/40'
                    }`}
                  >
                    {s.time}
                  </button>
                );
              })}
            </div>
          )}
        </div>

        <div>
          <h3 className="text-sm font-medium mb-2">Duration</h3>
          <div className="flex gap-2">
            {[1, 2, 3].map((n) => {
              const sel = durationSlots === n;
              const mins = n * slotMinutes;
              return (
                <button
                  key={n}
                  onClick={() => setDurationSlots(n)}
                  className={`flex-1 h-11 rounded-xl text-sm ${
                    sel ? 'bg-[#1A6B4F] text-white' : 'bg-card border border-border'
                  }`}
                >
                  {mins >= 60 ? `${mins / 60}h` : `${mins}m`}
                </button>
              );
            })}
          </div>
        </div>

        {rentalItems.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-2">Add a rental</h3>
            <div className="space-y-2">
              {rentalItems.map((item) => {
                const qty = rentalQty[item.id] ?? 0;
                const max = item.max_quantity ?? 99;
                const multiplier = item.price_unit === 'per_hour' ? durationHours : 1;
                const subtotal = Number(item.unit_price) * Math.max(1, qty) * multiplier;
                const unit = item.price_unit === 'per_hour'
                  ? `${item.currency} ${Number(item.unit_price).toFixed(0)}/hr`
                  : item.price_unit === 'per_can'
                    ? `${item.currency} ${Number(item.unit_price).toFixed(0)}/can`
                    : `${item.currency} ${Number(item.unit_price).toFixed(0)}/piece`;
                return (
                  <div
                    key={item.id}
                    className={`bg-card border border-border rounded-2xl p-4 flex items-center gap-3 ${
                      qty > 0 ? 'ring-2 ring-[#1A6B4F]/20' : ''
                    }`}
                  >
                    <div className="text-2xl">{item.icon ?? '🎾'}</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium">{item.name}</div>
                      {item.description && (
                        <div className="text-xs text-muted-foreground truncate">{item.description}</div>
                      )}
                      <div className="text-xs text-[#1A6B4F] mt-0.5">
                        {unit}{qty > 0 ? ` · ${item.currency} ${subtotal.toFixed(0)} total` : ''}
                      </div>
                    </div>
                    {qty === 0 ? (
                      <button
                        onClick={() => setRentalQty({ ...rentalQty, [item.id]: 1 })}
                        className="h-9 px-4 rounded-lg bg-[#D4F04A] hover:bg-[#b5e605] text-[#1a1a1a] text-sm"
                      >
                        Add
                      </button>
                    ) : (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setRentalQty({ ...rentalQty, [item.id]: Math.max(0, qty - 1) })}
                          className="w-9 h-9 rounded-lg bg-secondary hover:bg-muted flex items-center justify-center"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-6 text-center text-sm font-medium">{qty}</span>
                        <button
                          onClick={() => setRentalQty({ ...rentalQty, [item.id]: Math.min(max, qty + 1) })}
                          disabled={qty >= max}
                          className="w-9 h-9 rounded-lg bg-secondary hover:bg-muted flex items-center justify-center disabled:opacity-40"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="bg-card border border-border rounded-2xl p-4 flex items-start gap-3 text-xs text-muted-foreground">
          <ShieldCheck className="w-4 h-4 text-[#1A6B4F] flex-shrink-0 mt-0.5" />
          <span>
            Price is calculated per minute across peak/off-peak bands. You'll see the final total
            before paying.
          </span>
        </div>

        {reviewList.length > 0 && (
          <div>
            <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
              <Star className="w-4 h-4 fill-[#D4F04A] text-[#D4F04A]" />
              {avgRating?.toFixed(1)} · {reviewList.length} {reviewList.length === 1 ? 'review' : 'reviews'}
            </h3>
            <div className="space-y-2">
              {reviewList.slice(0, 5).map((r: any) => (
                <div key={r.id} className="bg-card border border-border rounded-2xl p-3">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="flex">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star
                          key={n}
                          className={`w-3.5 h-3.5 ${
                            n <= r.rating ? 'fill-[#D4F04A] text-[#D4F04A]' : 'text-border'
                          }`}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {r.profiles?.full_name ?? 'Deuce player'} · {new Date(r.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                  </div>
                  {r.comment && <p className="text-sm">{r.comment}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="fixed bottom-20 left-0 right-0 bg-background/95 backdrop-blur-sm border-t border-border px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex-shrink-0 mr-auto">
            <div className="text-[11px] text-muted-foreground leading-none">Total</div>
            <div className="text-base text-[#1A6B4F] font-medium leading-tight">
              {quote
                ? `${quote.currency} ${(quote.total + rentalTotal).toFixed(0)}`
                : quoting ? '…' : '—'}
            </div>
            {rentalTotal > 0 && quote && (
              <div className="text-[11px] text-muted-foreground leading-none mt-0.5">
                incl. rentals · {quote.currency} {rentalTotal.toFixed(0)}
              </div>
            )}
          </div>
          <button
            disabled={!selectedSlot || !startAt || !endAt || !quote}
            onClick={() => startAt && endAt && onBook({
              courtId: c.id,
              startAt,
              endAt,
              clubId: c.club_id,
              rentals: rentalsArray.length > 0 ? rentalsArray : undefined,
            })}
            className="flex-1 h-12 rounded-xl bg-[#1A6B4F] hover:bg-[#14563F] text-white text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
}

function Spec({ icon: Icon, label }: { icon: any; label: string }) {
  return (
    <div className="flex items-center gap-1 px-2.5 py-1 rounded-full bg-secondary text-xs capitalize">
      <Icon className="w-3 h-3" />
      {label}
    </div>
  );
}
